import { categoryStringRoute, isRootCategory } from '../../../lib/categories/categoriesLib';
import { Column, Title } from '@ydistri/ds';
import { ColoredHeadlineRow } from '../../../screens/Modals/CategoryExceptionsModal/CategoryExceptionsModal';
import ResetFormCategory from '../../../screens/Modals/CategoryExceptionsModal/ResetFormCategory';
import { List } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { CategoryId, PartialExceptionExpanded } from '../../../lib/categories/categoryTypes';
import {
  forcedTitleAsFieldType,
  getExceptionsBy,
  keyGetterCategoryId,
  renderer,
} from '../../../screens/Modals/CategoryExceptionsModal/categoryExceptionsModalLib';
import { useExceptions } from '../../../hooks/useExceptions';
import { sortConfigurationFieldTypes } from '../../../apis/applicationLib';
import { useGetCategoryConfigurationDefinitionsQuery } from '../../../apis/apiApplication';
import { useGetCategoryTreeQuery } from '../CategoryTree/apiCategoryTree';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

interface ExceptionsByCategoryListingProps {
  categoryId: CategoryId;
}

const ExceptionsByCategoryListing: React.FC<ExceptionsByCategoryListingProps> = ({
  categoryId,
}) => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();
  const { data: categories } = useGetCategoryTreeQuery(templateOrCalculation.value);

  const exceptions = useExceptions(categoryId);
  const { data: configurationDefinitions } = useGetCategoryConfigurationDefinitionsQuery();

  const sortedFieldTypes = useMemo(
    () => sortConfigurationFieldTypes(configurationDefinitions ?? []),
    [configurationDefinitions],
  );

  const exceptionsByCategory = useMemo(() => {
    const sorter = (a: PartialExceptionExpanded, b: PartialExceptionExpanded) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the sortedFieldTypes.obj may not always contain a or b fieldType
      return (sortedFieldTypes.obj[a.fieldType] ?? 0) - (sortedFieldTypes.obj[b.fieldType] ?? 0);
    };
    return getExceptionsBy(exceptions, keyGetterCategoryId, sorter);
  }, [sortedFieldTypes, exceptions]);

  const sortedCategoriesWithExceptions = useMemo(() => {
    return Object.entries(exceptionsByCategory)
      .map(([cid]) => {
        return {
          categoryId: Number(cid),
          titleFull: exceptionsByCategory[Number(cid)]?.[0]?.titleFull ?? '',
        };
      })
      .sort((a, b) => a.titleFull.localeCompare(b.titleFull));
  }, [exceptionsByCategory]);

  const listItemRenderer = useCallback(
    (exceptionExpanded: PartialExceptionExpanded) =>
      renderer(
        exceptionExpanded,
        forcedTitleAsFieldType,
        configurationDefinitions ?? [],
        undefined,
        false,
        !isConfigurationEditable,
      ),
    [configurationDefinitions, isConfigurationEditable],
  );

  return sortedCategoriesWithExceptions.map(({ categoryId }) => {
    const exceptions = exceptionsByCategory[categoryId];

    if ((exceptions?.length ?? 0) === 0) return null;
    if (isRootCategory(categoryId)) return null;

    return (
      <Column key={categoryId}>
        <ColoredHeadlineRow $justifyContent="space-between">
          <Title $size="small" $marginTop="small" $marginBottom="none">
            {categoryStringRoute(categories?.[categoryId])}
          </Title>
          <ResetFormCategory categoryId={categoryId} />
        </ColoredHeadlineRow>
        <List
          dataSource={exceptionsByCategory[categoryId]}
          loading={false}
          size="small"
          renderItem={listItemRenderer}
          data-testid={`ConfigurationGroupExceptionsByCategory-${categoryId}`}
        />
      </Column>
    );
  });
};

export default ExceptionsByCategoryListing;
